/**
 * @file CommonInput.tsx
 * @description
 * High-performance, premium input component. 
 * Fixed for Web to remove default browser outlines and internal textboxes.
 */

import React, { useState } from "react";
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TextInputProps,
  Platform,
  StyleProp,
  ViewStyle,
} from "react-native";
import { FontFamily } from "../theme/typography";
import { useTheme } from "../context/themeContext";
import type { ColorScheme } from "../theme/colors";

/* ================= TYPES ================= */

export type CommonInputProps = Readonly<{
  label?: string;
  value: string;
  placeholder?: string;
  error?: string;
  touched?: boolean;
  onChangeText?: (text: string) => void;
  isValid?: (value: string) => boolean;
  secureTextEntry?: boolean;
  rightIcon?: React.ReactNode;
  leftIcon?: React.ReactNode;
  disableCopyPaste?: boolean;
  inputProps?: TextInputProps;
  editable?: boolean;
  isDark?: boolean;
  isReadOnly?: boolean;
  badge?: string;
  containerStyle?: StyleProp<ViewStyle>;
}>;

/* ================= COMPONENT ================= */

/* ================= HELPER COMPONENTS ================= */

const InputLabel = ({ label, colors, isReadOnly, badge }: {
  label?: string;
  colors: ColorScheme;
  isReadOnly: boolean;
  badge?: string;
}) => {
  if (!label) return null;
  return (
    <View style={styles.labelRow}>
      <Text style={[styles.label, { color: colors.ink3 }]}>{label}</Text>
      {(isReadOnly || badge) && (
        <View style={[styles.badgeWrapper, { backgroundColor: colors.paper2, borderColor: colors.line }]}>
          <Text style={[styles.badgeText, { color: colors.ink3 }]}>{badge ?? "SECURED"}</Text>
        </View>
      )}
    </View>
  );
};

/* ================= COMPONENT ================= */

export default function CommonInput({
  label,
  value,
  placeholder,
  error,
  touched = false,
  onChangeText,
  isValid = () => true,
  secureTextEntry = false,
  rightIcon,
  leftIcon,
  disableCopyPaste = false,
  inputProps = {},
  editable = true,
  isReadOnly = false,
  badge,
  containerStyle,
}: CommonInputProps) {
  const { theme } = useTheme();
  const { colors } = theme;

  const [focused, setFocused] = useState(false);
  const [blurred, setBlurred] = useState(false);
  const { style: customStyle, ...restInputProps } = inputProps;

  const hasValue = value?.trim().length > 0;
  const isInputValid = isValid(value);
  const showError = (!isInputValid || !!error) && (blurred || touched || hasValue);

  // Determine text color based on state
  let textColor = colors.ink;
  if (showError) textColor = colors.danger;

  const getWrapperStyles = () => {
    const baseWrapper = {
      backgroundColor: colors.card,
      borderColor: colors.lineStrong,
    };
    const activeBorder = { borderColor: colors.accent, backgroundColor: colors.accentSoft };
    const errorBorder = { borderColor: colors.danger, backgroundColor: colors.dangerSoft };
    const disabled = { backgroundColor: colors.paper2, borderColor: colors.lineStrong };
    return [
      styles.inputWrapper,
      baseWrapper,
      focused && activeBorder,
      showError && errorBorder,
      (isReadOnly || !editable) && [styles.disabledWrapper, disabled],
    ];
  };

  return (
    <View style={[styles.container, containerStyle]}>
      <InputLabel label={label} colors={colors} isReadOnly={isReadOnly} badge={badge} />

      <View style={[getWrapperStyles(), rightIcon ? styles.wrapperWithRight : undefined]}>

        <TextInput
          value={value}
          placeholder={placeholder}
          placeholderTextColor={colors.ink4}
          secureTextEntry={secureTextEntry}
          onChangeText={onChangeText}
          contextMenuHidden={disableCopyPaste}
          editable={editable && !isReadOnly}
          underlineColorAndroid="transparent"
          autoComplete="off"
          autoCorrect={false}
          {...(Platform.OS === 'web' ? { autoComplete: 'new-password' } as any : {})}
          style={[
            styles.input,
            {
              color: textColor,
            },
            Platform.OS === 'web' && { outlineStyle: 'none', borderWidth: 0 } as any,
            Platform.OS === 'android' && {
              fontFamily: "", // Forces system default
              fontWeight: undefined, // Fixes emoji fallback on some Android devices
            },
            customStyle,
          ]}

          {...restInputProps}
          onFocus={(e) => {
            setFocused(true);
            inputProps.onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            setBlurred(true);
            inputProps.onBlur?.(e);
          }}
        />
        {rightIcon && (
          <View style={styles.iconWrapper}>
            {rightIcon}
          </View>
        )}
      </View>
      {showError && !!error && (
        <Text style={[styles.errorText, { color: colors.danger }]}>{error}</Text>
      )}
    </View>
  );
}

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  container: { marginBottom: 18, width: "100%" },
  label: {
    fontWeight: "800",
    fontSize: 12,
    fontFamily: FontFamily.sans,
    textTransform: "uppercase",
    letterSpacing: 1.5,
  },
  labelRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
    paddingHorizontal: 4,
  },
  badgeWrapper: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 2,
    borderWidth: 1,
  },
  badgeText: {
    fontSize: 9,
    fontWeight: "900",
    letterSpacing: 1,
  },
  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    minHeight: 42,
    borderWidth: 1.5,
    borderRadius: 2,
    paddingHorizontal: 12,
    paddingVertical: 6,
    // Clip long values so the text can't spill past the rounded border.
    overflow: "hidden",
    ...Platform.select({
      ios: {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.03, shadowRadius: 4,
      },
      android: { elevation: 1 },
      web: {
        transition: "all 0.2s ease-in-out",
        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.04)",
      } as any,
    }),
  },
  input: {
    flex: 1,
    // minWidth:0 lets the flex child shrink below its content width — without
    // it, a long value (e.g. a long first name) pushes past the box on web.
    minWidth: 0,
    fontSize: 14,
    fontWeight: "600",
    fontFamily: FontFamily.sans,
    height: "100%",
    paddingVertical: 0,
    backgroundColor: "transparent",
  },
  disabledWrapper: {
    opacity: 0.7,
  },
  wrapperWithRight: { paddingRight: 56 },
  iconWrapper: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    width: 52,
  },
  errorText: {
    fontSize: 12,
    marginTop: 6,
    fontWeight: "600",
    marginLeft: 4,
  },
});
